import { useEffect, useRef } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconPhoneFilled } from '@iwoca/icons';
import { Button } from '@iwoca/orion';

import { isGiveUsACallVisible } from './visible';
import {
  iwocapayTelDisplay,
  iwocapayTelLink,
} from '../../../../../src/constants.json';
import {
  useGetFundingRequirement,
  useGetProfile,
} from '../../../../api/lending/lapiHooks';
import logger from '../../../../utils/logger';
import { Card } from '../../../components/Card/Card';
import { LoadingCard } from '../../../components/LoadingCard/LoadingCard';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useNavigateToNextRequirement } from '../../hooks/useNavigateToNextRequirement';

export const GiveUsACall = () => {
  const hasLoggedRef = useRef(false);
  const { profile, loadingProfile } = useGetProfile();
  const { goToNextRequirement } = useNavigateToNextRequirement();

  // Refetch funding requirements when this page loads, to make sure we actually have to show the give-us-a-call page.
  // Funding blocks may have been resolved in the background by AMs, and the user would be visiting this page with stale data.
  const { fundingRequirement, fetchingFundingRequirement } =
    useGetFundingRequirement({ queryOptions: { refetchOnMount: 'always' } });

  useEffect(() => {
    if (fetchingFundingRequirement) return;

    if (fundingRequirement && isGiveUsACallVisible({ fundingRequirement })) {
      // Ensure we only log this message once.
      if (!hasLoggedRef.current && !loadingProfile) {
        logger.info('Give us a call shown to user', {
          user_id: profile.user_id,
          failing_funding_requirements: fundingRequirement.filter(
            (requirement) =>
              !requirement.is_optional && requirement.status !== 'completed',
          ),
        });
        hasLoggedRef.current = true;
      }
      return;
    }

    goToNextRequirement();
  }, [
    fundingRequirement,
    fetchingFundingRequirement,
    goToNextRequirement,
    profile.user_id,
    loadingProfile,
  ]);

  const trackGiveUsACallClicked = () => {
    tracking.ampli.buyerClickedGiveUsACallCta();
    return true;
  };

  if (fetchingFundingRequirement) return <LoadingCard />;

  return (
    <Card title="One last thing — give us a quick call">
      <WhiteLabelOverride componentType="button" disabled={false}>
        <Button
          className="my-s"
          type="primary"
          href={iwocapayTelLink}
          onClick={trackGiveUsACallClicked}
        >
          <IconPhoneFilled color="white" className="ml-xs" />
          {iwocapayTelDisplay}
        </Button>
      </WhiteLabelOverride>
      <p className="mb-0 text-m font-reg">
        Call your account manager to finish the last step.
      </p>
    </Card>
  );
};
